import {
  Page,
  Layout,
  Card,
  ResourceList,
  ResourceItem,
  Text,
  Thumbnail,
  Badge,
  BlockStack,
  InlineStack,
  Banner,
  Spinner,
  EmptyState,
  Button,
  Modal,
  Select,
  Box,
  DropZone,
  TextField,
} from '@shopify/polaris';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../lib/api';
import { safeImageUrl } from '../lib/url';

interface SyncedProduct {
  shopify_product_id: string;
  title: string;
  product_type?: string;
  vendor?: string;
  garment_image_url?: string;
  category?: string;
  tryon_enabled: boolean;
  tryon_count?: number;
  status: string;
}

interface SyncedProductsResponse {
  products: SyncedProduct[];
  count: number;
}

interface SyncResponse {
  success: boolean;
  totalProducts: number;
  eligibleProducts: number;
  syncedProducts: number;
}

interface ToggleResponse {
  success: boolean;
  product: { shopify_product_id: string; title: string; tryon_enabled: boolean };
}

interface StartGenerationResponse {
  success: boolean;
  data?: { job_id: string };
  error?: { code: string; message: string };
}

interface GenerationStatusResponse {
  success: boolean;
  data?: { status: 'processing' | 'completed' | 'failed'; result_url?: string; job_id: string };
  error?: { code: string; message: string };
}

interface PublishResponse {
  success: boolean;
  data?: { media_gid: string; alreadyPublished: boolean };
  error?: { code: string; message: string };
}

function normalizeProducts(raw: unknown): SyncedProduct[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (p): p is SyncedProduct => !!p && p.shopify_product_id != null,
  );
}

/**
 * The three merchant AI tools (Generate Model Photo, Swap Model, Recolor) are hidden until their
 * backends are reliable in production (#2054): Recolor fails (Vertex key file missing, no GCP
 * billing: "Recolor failed. Please try again."), Generate Model Photo hangs in the
 * product-to-model worker (observed 22.10.2026: job stays "processing", no BFL call after the
 * garment image is fetched) and Swap Model shares that stack. The reviewer's 2.1.1 screencast
 * shows these buttons failing or spinning forever. Set VITE_MERCHANT_AI_TOOLS_ENABLED=true at
 * build time to bring them back.
 */
export const MERCHANT_AI_TOOLS_ENABLED = import.meta.env.VITE_MERCHANT_AI_TOOLS_ENABLED === 'true';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 3 * 60 * 1000; // matches api-router's own generation budget

export default function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<SyncedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResponse | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Product-to-Model modal state
  const [modalProduct, setModalProduct] = useState<SyncedProduct | null>(null);
  const [modelType, setModelType] = useState('female');
  const [generating, setGenerating] = useState(false);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  // A monotonically increasing token, not a boolean. A single shared
  // boolean flag would race if a merchant closes the modal and immediately
  // reopens it for a DIFFERENT product: false -> true -> false, and a
  // pending setTimeout from the FIRST product's poll loop would see `false`
  // again and keep running, eventually overwriting the second product's
  // modal state with the first product's result. Each poll session captures
  // its own token at start and checks it's still current before proceeding.
  const pollSessionRef = useRef(0);

  // Model Swap modal state — separate from the Product-to-Model modal above
  // (different upstream contract: needs a merchant-uploaded reference model
  // image, no modelType select). Same monotonic-token poll-session pattern.
  const [swapModalProduct, setSwapModalProduct] = useState<SyncedProduct | null>(null);
  const [swapModelImage, setSwapModelImage] = useState<string | null>(null);
  const [swapGenerating, setSwapGenerating] = useState(false);
  const [swapResultUrl, setSwapResultUrl] = useState<string | null>(null);
  const [swapJobId, setSwapJobId] = useState<string | null>(null);
  const [swapPublishing, setSwapPublishing] = useState(false);
  const [swapPublished, setSwapPublished] = useState(false);
  const [swapModalError, setSwapModalError] = useState<string | null>(null);
  const swapPollSessionRef = useRef(0);

  // Recolor modal state — async job/poll pair, same as Model Swap (see
  // recolor.ts docstring: api-router's own Vertex call is too close to the
  // ALB's 120s idle_timeout to round-trip synchronously through the
  // browser). Same monotonic-token poll-session pattern as the other two.
  const [recolorModalProduct, setRecolorModalProduct] = useState<SyncedProduct | null>(null);
  const [recolorSelectPrompt, setRecolorSelectPrompt] = useState('');
  const [recolorTargetColor, setRecolorTargetColor] = useState('');
  const [recolorGenerating, setRecolorGenerating] = useState(false);
  const [recolorResultUrl, setRecolorResultUrl] = useState<string | null>(null);
  const [recolorModalError, setRecolorModalError] = useState<string | null>(null);
  const recolorPollSessionRef = useRef(0);

  const loadProducts = useCallback(async (opts?: { throwOnError?: boolean }) => {
    setError(null);
    try {
      const data = await apiGet<SyncedProductsResponse>('/api/products/synced');
      setProducts(normalizeProducts(data?.products));
    } catch (err) {
      console.error('[Products] failed to load synced products:', err);
      if (opts?.throwOnError) throw err;
      setError('Could not load products. Please reload the page or reinstall the app.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts().catch(() => {});
  }, [loadProducts]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);
    try {
      const result = await apiPost<SyncResponse>('/api/products/sync', {});
      await loadProducts({ throwOnError: true });
      setSyncResult(result);
    } catch (err) {
      console.error('[Products] sync failed:', err);
      setError('Failed to sync products. Please try again.');
      setSyncResult(null);
    } finally {
      setSyncing(false);
    }
  };

  const handleToggle = async (product: SyncedProduct) => {
    const id = product.shopify_product_id;
    const originalEnabled = product.tryon_enabled;
    setTogglingId(id);
    // Optimistic update
    setProducts((prev) =>
      prev.map((p) => (p.shopify_product_id === id ? { ...p, tryon_enabled: !originalEnabled } : p)),
    );
    try {
      const result = await apiPut<ToggleResponse>(
        `/api/products/${id}/tryon-enabled`,
        { enabled: !originalEnabled },
      );
      if (result.success) {
        setProducts((prev) =>
          prev.map((p) =>
            p.shopify_product_id === id
              ? { ...p, tryon_enabled: result.product.tryon_enabled }
              : p,
          ),
        );
      }
    } catch (err) {
      console.error('[Products] toggle failed:', err);
      // Revert optimistic update on failure
      setProducts((prev) =>
        prev.map((p) => (p.shopify_product_id === id ? { ...p, tryon_enabled: originalEnabled } : p)),
      );
      setError(`Failed to update "${product.title}". Please try again.`);
    } finally {
      setTogglingId(null);
    }
  };

  const openGenerateModal = (product: SyncedProduct) => {
    pollSessionRef.current += 1;
    setModalProduct(product);
    setModelType('female');
    setResultUrl(null);
    setJobId(null);
    setPublished(false);
    setModalError(null);
    setGenerating(false);
  };

  const closeGenerateModal = () => {
    pollSessionRef.current += 1; // invalidates any in-flight poll loop
    setModalProduct(null);
  };

  // Takes shopifyProductId and jobId explicitly rather than reading
  // modalProduct from closure/state — modalProduct can change (a merchant
  // closes this modal and opens another product's) while a setTimeout from
  // an earlier poll session is still pending; reading current state at that
  // point would send the WRONG product ID to the status endpoint, not just
  // display a stale result. `session` is captured once per poll chain and
  // checked against the live counter before every state update or
  // reschedule, so a stale chain silently stops instead of touching state.
  //
  // Uses apiPost (lib/api.ts), which fetches a fresh Shopify session token
  // per call — safe against the ~60s App Bridge token lifetime even though
  // polling can run for minutes.
  const pollStatus = async (
    shopifyProductId: string,
    jobId: string,
    deadline: number,
    session: number,
  ): Promise<void> => {
    if (pollSessionRef.current !== session) return;
    if (Date.now() > deadline) {
      setModalError('Generation is taking longer than expected. Please try again.');
      setGenerating(false);
      return;
    }
    try {
      const status = await apiPost<GenerationStatusResponse>(
        `/api/products/${shopifyProductId}/generate-model-photo/status`,
        { job_id: jobId },
      );
      if (pollSessionRef.current !== session) return;
      if (status.data?.status === 'completed' && status.data.result_url) {
        setResultUrl(status.data.result_url);
        setGenerating(false);
        return;
      }
      if (status.data?.status === 'failed' || (!status.success && status.error)) {
        setModalError(status.error?.message || 'Generation failed. Please try again.');
        setGenerating(false);
        return;
      }
    } catch (err) {
      // Transient — keep polling, same reasoning as the server-side status handler.
      console.error('[Products] poll error (will retry):', err);
    }
    // The modal may have been closed (or reopened on another product) while the
    // status request was in flight — including inside the catch above. Bail before
    // arming the next tick so a dead chain leaves no timer behind at all.
    if (pollSessionRef.current !== session) return;
    setTimeout(() => pollStatus(shopifyProductId, jobId, deadline, session), POLL_INTERVAL_MS);
  };

  const handleGenerate = async () => {
    if (!modalProduct) return;
    const shopifyProductId = modalProduct.shopify_product_id;
    const session = pollSessionRef.current;
    setGenerating(true);
    setModalError(null);
    setResultUrl(null);
    try {
      const start = await apiPost<StartGenerationResponse>(
        `/api/products/${shopifyProductId}/generate-model-photo`,
        { modelType },
      );
      if (!start.success || !start.data?.job_id) {
        setModalError(start.error?.message || 'Failed to start generation.');
        setGenerating(false);
        return;
      }
      setJobId(start.data.job_id);
      pollStatus(shopifyProductId, start.data.job_id, Date.now() + POLL_TIMEOUT_MS, session);
    } catch (err) {
      console.error('[Products] generate failed:', err);
      setModalError('Failed to start generation. Please try again.');
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (!modalProduct || !jobId) return;
    setPublishing(true);
    setModalError(null);
    try {
      const result = await apiPost<PublishResponse>(
        `/api/products/${modalProduct.shopify_product_id}/generate-model-photo/publish`,
        { job_id: jobId },
      );
      if (!result.success) {
        setModalError(result.error?.message || 'Failed to add photo to product gallery.');
        return;
      }
      setPublished(true);
    } catch (err) {
      console.error('[Products] publish failed:', err);
      setModalError('Failed to add photo to product gallery. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const openSwapModal = (product: SyncedProduct) => {
    swapPollSessionRef.current += 1;
    setSwapModalProduct(product);
    setSwapModelImage(null);
    setSwapResultUrl(null);
    setSwapJobId(null);
    setSwapPublished(false);
    setSwapModalError(null);
    setSwapGenerating(false);
  };

  const closeSwapModal = () => {
    swapPollSessionRef.current += 1; // invalidates any in-flight poll loop
    setSwapModalProduct(null);
  };

  const MAX_SWAP_IMAGE_BYTES = 10 * 1024 * 1024; // matches the server's uploadBase64Image cap

  const handleSwapImageDrop = useCallback((_dropFiles: File[], acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    // Reject oversized files before FileReader encodes them to base64 —
    // a large raw image can freeze the tab during encoding, and would be
    // rejected server-side anyway (flagged by cross-review).
    if (file.size > MAX_SWAP_IMAGE_BYTES) {
      setSwapModalError('Image is too large (max 10MB). Please choose a smaller file.');
      return;
    }
    setSwapModalError(null);
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setSwapModelImage(reader.result);
    };
    reader.onerror = () => setSwapModalError('Failed to read the selected image. Please try another file.');
    reader.readAsDataURL(file);
  }, []);

  // Same reasoning as pollStatus above — session-token guard against a
  // stale poll chain overwriting a different product's modal state.
  const pollSwapStatus = async (
    shopifyProductId: string,
    jobId: string,
    deadline: number,
    session: number,
  ): Promise<void> => {
    if (swapPollSessionRef.current !== session) return;
    if (Date.now() > deadline) {
      setSwapModalError('Generation is taking longer than expected. Please try again.');
      setSwapGenerating(false);
      return;
    }
    try {
      const status = await apiPost<GenerationStatusResponse>(
        `/api/products/${shopifyProductId}/swap-model/status`,
        { job_id: jobId },
      );
      if (swapPollSessionRef.current !== session) return;
      if (status.data?.status === 'completed' && status.data.result_url) {
        setSwapResultUrl(status.data.result_url);
        setSwapGenerating(false);
        return;
      }
      if (status.data?.status === 'failed' || (!status.success && status.error)) {
        setSwapModalError(status.error?.message || 'Model swap failed. Please try again.');
        setSwapGenerating(false);
        return;
      }
    } catch (err) {
      console.error('[Products] swap poll error (will retry):', err);
    }
    // The modal may have been closed (or reopened on another product) while the
    // status request was in flight — including inside the catch above. Bail before
    // arming the next tick so a dead chain leaves no timer behind at all.
    if (swapPollSessionRef.current !== session) return;
    setTimeout(() => pollSwapStatus(shopifyProductId, jobId, deadline, session), POLL_INTERVAL_MS);
  };

  const handleSwapGenerate = async () => {
    if (!swapModalProduct || !swapModelImage) return;
    const shopifyProductId = swapModalProduct.shopify_product_id;
    const session = swapPollSessionRef.current;
    setSwapGenerating(true);
    setSwapModalError(null);
    setSwapResultUrl(null);
    try {
      const start = await apiPost<StartGenerationResponse>(
        `/api/products/${shopifyProductId}/swap-model`,
        { modelImage: swapModelImage },
      );
      if (!start.success || !start.data?.job_id) {
        setSwapModalError(start.error?.message || 'Failed to start model swap.');
        setSwapGenerating(false);
        return;
      }
      setSwapJobId(start.data.job_id);
      pollSwapStatus(shopifyProductId, start.data.job_id, Date.now() + POLL_TIMEOUT_MS, session);
    } catch (err) {
      console.error('[Products] swap generate failed:', err);
      setSwapModalError('Failed to start model swap. Please try again.');
      setSwapGenerating(false);
    }
  };

  const handleSwapPublish = async () => {
    if (!swapModalProduct || !swapJobId) return;
    setSwapPublishing(true);
    setSwapModalError(null);
    try {
      const result = await apiPost<PublishResponse>(
        `/api/products/${swapModalProduct.shopify_product_id}/swap-model/publish`,
        { job_id: swapJobId },
      );
      if (!result.success) {
        setSwapModalError(result.error?.message || 'Failed to add photo to product gallery.');
        return;
      }
      setSwapPublished(true);
    } catch (err) {
      console.error('[Products] swap publish failed:', err);
      setSwapModalError('Failed to add photo to product gallery. Please try again.');
    } finally {
      setSwapPublishing(false);
    }
  };

  const openRecolorModal = (product: SyncedProduct) => {
    recolorPollSessionRef.current += 1;
    setRecolorModalProduct(product);
    setRecolorSelectPrompt('');
    setRecolorTargetColor('');
    setRecolorResultUrl(null);
    setRecolorModalError(null);
    setRecolorGenerating(false);
  };

  const closeRecolorModal = () => {
    recolorPollSessionRef.current += 1; // invalidates any in-flight poll loop
    setRecolorModalProduct(null);
  };

  const pollRecolorStatus = async (
    shopifyProductId: string,
    jobId: string,
    deadline: number,
    session: number,
  ): Promise<void> => {
    if (recolorPollSessionRef.current !== session) return;
    if (Date.now() > deadline) {
      setRecolorModalError('Recolor is taking longer than expected. Please try again.');
      setRecolorGenerating(false);
      return;
    }
    try {
      const status = await apiPost<GenerationStatusResponse>(
        `/api/products/${shopifyProductId}/recolor/status`,
        { job_id: jobId },
      );
      if (recolorPollSessionRef.current !== session) return;
      if (status.data?.status === 'completed' && status.data.result_url) {
        setRecolorResultUrl(status.data.result_url);
        setRecolorGenerating(false);
        return;
      }
      if (status.data?.status === 'failed' || (!status.success && status.error)) {
        setRecolorModalError(status.error?.message || 'Recolor failed. Please try again.');
        setRecolorGenerating(false);
        return;
      }
    } catch (err) {
      console.error('[Products] recolor poll error (will retry):', err);
    }
    // The modal may have been closed (or reopened on another product) while the
    // status request was in flight — including inside the catch above. Bail before
    // arming the next tick so a dead chain leaves no timer behind at all.
    if (recolorPollSessionRef.current !== session) return;
    setTimeout(() => pollRecolorStatus(shopifyProductId, jobId, deadline, session), POLL_INTERVAL_MS);
  };

  const handleRecolorGenerate = async () => {
    if (!recolorModalProduct || !recolorTargetColor.trim()) return;
    const shopifyProductId = recolorModalProduct.shopify_product_id;
    const session = recolorPollSessionRef.current;
    setRecolorGenerating(true);
    setRecolorModalError(null);
    setRecolorResultUrl(null);
    try {
      const start = await apiPost<StartGenerationResponse>(
        `/api/products/${shopifyProductId}/recolor`,
        { selectPrompt: recolorSelectPrompt.trim() || undefined, recolorPrompt: recolorTargetColor.trim() },
      );
      if (!start.success || !start.data?.job_id) {
        setRecolorModalError(start.error?.message || 'Failed to start recolor.');
        setRecolorGenerating(false);
        return;
      }
      pollRecolorStatus(shopifyProductId, start.data.job_id, Date.now() + POLL_TIMEOUT_MS, session);
    } catch (err) {
      console.error('[Products] recolor start failed:', err);
      setRecolorModalError('Failed to start recolor. Please try again.');
      setRecolorGenerating(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spinner accessibilityLabel="Loading products" size="large" />
          </div>
        </Card>
      );
    }

    if (products.length === 0) {
      return (
        <Card>
          <EmptyState
            heading="No synced products yet"
            action={{ content: 'Sync Products', onAction: handleSync, loading: syncing }}
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>
              Click "Sync Products" to import your store's products. Try-on starts
              enabled for every product; you can switch it off per product.
            </p>
          </EmptyState>
        </Card>
      );
    }

    return (
      <Card>
        <ResourceList
          resourceName={{ singular: 'product', plural: 'products' }}
          items={products}
          renderItem={(item: SyncedProduct) => {
            const { shopify_product_id, title, vendor, garment_image_url, category, tryon_enabled } = item;
            const image = safeImageUrl(garment_image_url);
            const isToggling = togglingId === shopify_product_id;

            return (
              <ResourceItem id={shopify_product_id} onClick={() => {}}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <Thumbnail source={image || ''} alt={title} size="small" />
                    <BlockStack gap="100">
                      <Text as="h3" variant="bodyMd" fontWeight="bold">
                        {title}
                      </Text>
                      {vendor ? (
                        <Text as="p" variant="bodySm" tone="subdued">
                          {vendor}
                        </Text>
                      ) : null}
                      <InlineStack gap="200">
                        {tryon_enabled ? (
                          <Badge tone="success">Try-On Enabled</Badge>
                        ) : (
                          <Badge tone="attention">Try-On Disabled</Badge>
                        )}
                        {category ? (
                          <Badge>{category}</Badge>
                        ) : null}
                      </InlineStack>
                    </BlockStack>
                  </div>
                  <div style={{ flexShrink: 0, display: 'flex', gap: '8px' }}>
                    {MERCHANT_AI_TOOLS_ENABLED && (
                      <>
                        <Button size="slim" onClick={() => openGenerateModal(item)} disabled={!garment_image_url}>
                          Generate Model Photo
                        </Button>
                        <Button size="slim" onClick={() => openSwapModal(item)} disabled={!garment_image_url}>
                          Swap Model
                        </Button>
                        <Button size="slim" onClick={() => openRecolorModal(item)} disabled={!garment_image_url}>
                          Recolor
                        </Button>
                      </>
                    )}
                    <Button
                      variant={tryon_enabled ? 'secondary' : 'primary'}
                      size="slim"
                      loading={isToggling}
                      disabled={isToggling}
                      onClick={() => handleToggle(item)}
                    >
                      {tryon_enabled ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                </div>
              </ResourceItem>
            );
          }}
        />
      </Card>
    );
  };

  return (
    <Page
      title="Products"
      backAction={{ content: 'Dashboard', onAction: () => navigate('/') }}
      primaryAction={{
        content: 'Sync Products',
        onAction: handleSync,
        loading: syncing,
        disabled: loading,
      }}
    >
      <Layout>
        <Layout.Section>
          <Banner tone="info">
            <p>
              Sync your store's products, then enable virtual try-on per item.
              Only enabled products will show the "Try It On" button in your storefront.
            </p>
          </Banner>
        </Layout.Section>

        {error ? (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        ) : null}

        {syncResult ? (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSyncResult(null)}>
              <p>
                Synced successfully — {syncResult.eligibleProducts} of{' '}
                {syncResult.totalProducts} products are eligible for virtual try-on.
              </p>
            </Banner>
          </Layout.Section>
        ) : null}

        <Layout.Section>{renderContent()}</Layout.Section>
      </Layout>

      {modalProduct ? (
        <Modal
          open
          onClose={closeGenerateModal}
          title={`Generate model photo — ${modalProduct.title}`}
          primaryAction={
            resultUrl
              ? {
                  content: published ? 'Added to gallery' : 'Add to product gallery',
                  onAction: handlePublish,
                  loading: publishing,
                  disabled: published,
                }
              : { content: 'Generate', onAction: handleGenerate, loading: generating, disabled: generating }
          }
          secondaryActions={[{ content: 'Close', onAction: closeGenerateModal }]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              {modalError ? (
                <Banner tone="critical">
                  <p>{modalError}</p>
                </Banner>
              ) : null}

              {!resultUrl ? (
                <Select
                  label="Model type"
                  options={[
                    { label: 'Female', value: 'female' },
                    { label: 'Male', value: 'male' },
                    { label: 'Unisex', value: 'unisex' },
                  ]}
                  value={modelType}
                  onChange={setModelType}
                  disabled={generating}
                />
              ) : null}

              {generating && !resultUrl ? (
                <Box padding="800">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <Spinner accessibilityLabel="Generating" size="large" />
                    <Text as="p" tone="subdued">Generating your model photo — this can take up to a minute…</Text>
                  </div>
                </Box>
              ) : null}

              {resultUrl ? (
                <BlockStack gap="300">
                  <img
                    src={resultUrl}
                    alt="Generated model"
                    style={{ maxWidth: '100%', borderRadius: '8px' }}
                  />
                  {/* result_url is a cross-origin S3 URL — the `download`
                      attribute is not honored cross-origin by most browsers,
                      so this best-effort opens the image in a new tab (the
                      merchant can right-click → Save Image) rather than
                      guaranteeing a forced download. A true forced download
                      would need a same-origin proxy/blob fetch. */}
                  <Button url={resultUrl} external>
                    Download
                  </Button>
                </BlockStack>
              ) : null}
            </BlockStack>
          </Modal.Section>
        </Modal>
      ) : null}

      {swapModalProduct ? (
        <Modal
          open
          onClose={closeSwapModal}
          title={`Swap model — ${swapModalProduct.title}`}
          primaryAction={
            swapResultUrl
              ? {
                  content: swapPublished ? 'Added to gallery' : 'Add to product gallery',
                  onAction: handleSwapPublish,
                  loading: swapPublishing,
                  disabled: swapPublished,
                }
              : {
                  content: 'Swap Model',
                  onAction: handleSwapGenerate,
                  loading: swapGenerating,
                  disabled: swapGenerating || !swapModelImage,
                }
          }
          secondaryActions={[{ content: 'Close', onAction: closeSwapModal }]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              {swapModalError ? (
                <Banner tone="critical">
                  <p>{swapModalError}</p>
                </Banner>
              ) : null}

              {!swapResultUrl && !swapGenerating ? (
                <BlockStack gap="300">
                  <Text as="p" tone="subdued">
                    Upload a photo of the model you want to see wearing this product.
                  </Text>
                  <DropZone accept="image/*" type="image" onDrop={handleSwapImageDrop} allowMultiple={false}>
                    {swapModelImage ? (
                      <Box padding="400">
                        <img
                          src={swapModelImage}
                          alt="Reference model preview"
                          style={{ maxWidth: '100%', maxHeight: '240px', borderRadius: '8px', display: 'block', margin: '0 auto' }}
                        />
                      </Box>
                    ) : (
                      <DropZone.FileUpload actionHint="Accepts .jpg, .png" />
                    )}
                  </DropZone>
                </BlockStack>
              ) : null}

              {swapGenerating && !swapResultUrl ? (
                <Box padding="800">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <Spinner accessibilityLabel="Generating" size="large" />
                    <Text as="p" tone="subdued">Swapping the model — this can take up to a minute…</Text>
                  </div>
                </Box>
              ) : null}

              {swapResultUrl ? (
                <BlockStack gap="300">
                  <img
                    src={swapResultUrl}
                    alt="Model swap result"
                    style={{ maxWidth: '100%', borderRadius: '8px' }}
                  />
                  <Button url={swapResultUrl} external>
                    Download
                  </Button>
                </BlockStack>
              ) : null}
            </BlockStack>
          </Modal.Section>
        </Modal>
      ) : null}

      {recolorModalProduct ? (
        <Modal
          open
          onClose={closeRecolorModal}
          title={`Recolor — ${recolorModalProduct.title}`}
          primaryAction={
            recolorResultUrl
              ? { content: 'Done', onAction: closeRecolorModal }
              : {
                  content: 'Recolor',
                  onAction: handleRecolorGenerate,
                  loading: recolorGenerating,
                  disabled: recolorGenerating || !recolorTargetColor.trim(),
                }
          }
          secondaryActions={[{ content: 'Close', onAction: closeRecolorModal }]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              {recolorModalError ? (
                <Banner tone="critical">
                  <p>{recolorModalError}</p>
                </Banner>
              ) : null}

              {!recolorResultUrl && !recolorGenerating ? (
                <BlockStack gap="300">
                  <TextField
                    label="What to recolor (optional)"
                    placeholder="the garment"
                    value={recolorSelectPrompt}
                    onChange={setRecolorSelectPrompt}
                    autoComplete="off"
                  />
                  <TextField
                    label="Target color"
                    placeholder="e.g. navy blue"
                    value={recolorTargetColor}
                    onChange={setRecolorTargetColor}
                    autoComplete="off"
                    requiredIndicator
                  />
                </BlockStack>
              ) : null}

              {recolorGenerating && !recolorResultUrl ? (
                <Box padding="800">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <Spinner accessibilityLabel="Generating" size="large" />
                    <Text as="p" tone="subdued">Recoloring — this can take up to two minutes…</Text>
                  </div>
                </Box>
              ) : null}

              {recolorResultUrl ? (
                <BlockStack gap="300">
                  <img
                    src={recolorResultUrl}
                    alt="Recolored product"
                    style={{ maxWidth: '100%', borderRadius: '8px' }}
                  />
                  <Button url={recolorResultUrl} external>
                    Download
                  </Button>
                </BlockStack>
              ) : null}
            </BlockStack>
          </Modal.Section>
        </Modal>
      ) : null}
    </Page>
  );
}
